import { motion } from 'framer-motion';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getAvailablePrices,
  getCurrentSubscription,
  PriceInfo,
  SubscriptionStatus,
  KEY_MYGPT_TOKEN_EXPIRESIN,
} from '@mygpt/app';
import { showNotification } from '@mantine/notifications';
import { Modal, Button, Text, Group, Alert } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import classes from './index.module.css';

import { AuthChoiceModal } from '@/components/AuthChoiceModal';
import { OrderConfirmModal } from '@/components/OrderConfirmModal';
import { usePromoFromUrl } from '@/components/Pricing/usePromoFromUrl';

interface Tier {
  name: string;
  id: 'basic' | 'standard' | 'premium' | 'diamond' | 'enterprise';
  price: {
    monthly: string;
    yearly: string;
  };
  priceId?: string; // Stripe Price ID
  description: string;
  features: string[];
  cta: string;
  mostPopular: boolean;
}

interface Feature {
  name: string;
  tiers: {
    [key in Tier['id']]: string | boolean;
  };
}

/** 格式化数量：null → 無制限，数字 → 千分位 */
const formatQuota = (num: number | null): string => {
  if (num === null) return '無制限';
  return num.toLocaleString();
};

/** 格式化价格：千分位日元 */
const formatPrice = (amount: number): string => {
  return amount.toLocaleString();
};

const Price = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [paymentSuccessModalOpened, setPaymentSuccessModalOpened] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  // v0.5: 订单确认 Modal，与 /pricing 行为一致
  const urlPromo = usePromoFromUrl();
  const [confirmPriceId, setConfirmPriceId] = useState<string | null>(null);
  // v0.9.3: 未登录用户点订阅时弹出登录/注册选择
  const [authChoiceOpen, setAuthChoiceOpen] = useState(false);

  const [tiers, setTiers] = useState<Tier[]>([
    {
      id: 'basic',
      name: 'スターター',
      price: { monthly: '49,800', yearly: '547,800' },
      description: '小規模チーム向けの基本機能',
      features: [
        'ストレージ: 5GB',
        'ボット3台',
        '月2,000メッセージ',
        'ウェブサイト連携(100ページ)',
        'ドキュメント・FAQ連携',
        'メールサポート(9:00-18:00)',
      ],
      cta: '始める',
      mostPopular: false,
    },
    {
      id: 'standard',
      name: 'プロフェッショナル',
      price: { monthly: '99,800', yearly: '1,097,800' },
      description: '成長企業向けの高度な機能',
      features: [
        'ストレージ: 50GB',
        '20個ボット',
        '月5,000メッセージ',
        '完全なウェブサイト連携',
        'RAGチャットボットとAgent機能',
        '音声対話とキャンバス',
        'メールサポート(9:00-20:00)',
      ],
      cta: '始める',
      mostPopular: true,
    },
    {
      id: 'premium',
      name: 'ビジネス',
      price: { monthly: '149,800', yearly: '1,647,800' },
      description: '大規模組織向けの完全なソリューション',
      features: [
        'ストレージ: 100GB',
        '50個ボット',
        '月10,000メッセージ',
        'マルチモーダル分析(500項目)',
        'SaaS統合(最大5つ)',
        'スマートスクリーンモード',
        'メール+チャット+専任サポート',
      ],
      cta: '始める',
      mostPopular: false,
    },
    {
      id: 'diamond',
      name: 'ビジネスエリート',
      price: { monthly: '398,000', yearly: '4,378,000' },
      description: '高度な運用のためのエンタープライズグレード機能',
      features: [
        'ストレージ: 250GB',
        '130個ボット',
        '月30,000メッセージ',
        '無制限SaaS統合',
        '微調整サービス含む',
        'マルチモーダル分析(1000項目)',
        '24/7メール+チャットサポート',
      ],
      cta: '始める',
      mostPopular: false,
    },
    {
      id: 'enterprise',
      name: 'エンタープライズ',
      price: { monthly: 'お問い合わせ', yearly: 'お問い合わせ' },
      description: '大企業向けのカスタマイズされたソリューション',
      features: [
        '無制限ストレージ',
        '無制限ボット',
        '無制限メッセージ',
        'オンプレミス導入',
        'カスタム更新頻度',
        '24/7専任チーム+カスタマーサクセスマネージャー',
      ],
      cta: 'お問い合わせ',
      mostPopular: false,
    },
  ]);

  const [features, setFeatures] = useState<Feature[]>([
    {
      name: 'ストレージ',
      tiers: { basic: '5GB', standard: '50GB', premium: '100GB', diamond: '250GB', enterprise: '無制限' },
    },
    {
      name: 'ボット数',
      tiers: { basic: '3', standard: '20', premium: '50', diamond: '130', enterprise: '無制限' },
    },
    {
      name: '月間メッセージ数',
      tiers: {
        basic: '2,000件/月',
        standard: '5,000件/月',
        premium: '10,000件/月',
        diamond: '30,000件/月',
        enterprise: '無制限',
      },
    },
    {
      name: 'ウェブサイト連携',
      tiers: { basic: '100ページ', standard: '完全', premium: '完全', diamond: '完全', enterprise: '完全' },
    },
    {
      name: 'ドキュメント連携',
      tiers: { basic: true, standard: true, premium: true, diamond: true, enterprise: true },
    },
    {
      name: 'FAQ連携',
      tiers: { basic: true, standard: true, premium: true, diamond: true, enterprise: true },
    },
    {
      name: '音声対話',
      tiers: { basic: false, standard: true, premium: true, diamond: true, enterprise: true },
    },
    {
      name: 'RAGチャットボット',
      tiers: {
        basic: '1,000検索/月',
        standard: '完全',
        premium: '完全',
        diamond: '完全',
        enterprise: '完全',
      },
    },
    {
      name: 'エージェント機能',
      tiers: { basic: false, standard: true, premium: true, diamond: true, enterprise: true },
    },
    {
      name: 'マルチモーダル分析',
      tiers: {
        basic: false,
        standard: '200アイテム',
        premium: '500アイテム',
        diamond: '1,000アイテム',
        enterprise: '無制限',
      },
    },
    {
      name: 'スマートスクリーンモード',
      tiers: { basic: false, standard: true, premium: true, diamond: true, enterprise: true },
    },
    {
      name: 'インタラクティブキャンバス',
      tiers: { basic: false, standard: true, premium: true, diamond: true, enterprise: true },
    },
    {
      name: 'SaaS連携',
      tiers: { basic: false, standard: false, premium: '5サービス', diamond: '無制限', enterprise: '無制限' },
    },
    {
      name: '微調整サービス',
      tiers: { basic: false, standard: false, premium: '追加料金', diamond: true, enterprise: true },
    },
    {
      name: 'オンプレミス',
      tiers: { basic: false, standard: false, premium: false, diamond: false, enterprise: true },
    },
    {
      name: 'サポート時間',
      tiers: {
        basic: '9:00-18:00',
        standard: '9:00-20:00',
        premium: '9:00-20:00',
        diamond: '24/7',
        enterprise: '24/7専任',
      },
    },
    {
      name: 'サポートチャネル',
      tiers: {
        basic: 'メール',
        standard: 'メール',
        premium: 'メール+チャット+専任',
        diamond: 'メール+チャット+専任',
        enterprise: '専任チーム+カスタマーサクセス',
      },
    },
  ]);

  // 处理支付回调
  useEffect(() => {
    const payment = searchParams.get('payment');

    if (payment === 'success') {
      setPaymentSuccessModalOpened(true);
      searchParams.delete('payment');
      setSearchParams(searchParams);
    } else if (payment === 'cancel') {
      showNotification({
        color: 'orange',
        title: '支払いがキャンセルされました',
        message: 'お支払いをキャンセルしました。再度お試しください。',
      });
      searchParams.delete('payment');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  // 检查用户订阅状态
  useEffect(() => {
    const checkSubscription = async () => {
      const expiresTime = Number(Cookies.get(KEY_MYGPT_TOKEN_EXPIRESIN()));
      if (!expiresTime || expiresTime <= new Date().getTime()) return;

      try {
        const res = await getCurrentSubscription();
        const subscription = res.data?.subscription;
        const scheduledSub = res.data?.scheduled_subscription;

        if (!subscription || subscription.status !== SubscriptionStatus.ACTIVE) return;

        // 活跃且未取消 → 禁止
        if (!subscription.cancel_at_period_end) {
          setHasActiveSubscription(true);
          return;
        }

        // 已取消但有预订阅 → 也禁止
        if (subscription.cancel_at_period_end && scheduledSub) {
          setHasActiveSubscription(true);
        }
      } catch {
        // 未登录或请求失败，不影响页面展示
      }
    };

    checkSubscription();
  }, []);

  // 解析卡片月额字符串（如 "49,800"）为数字
  const parseMonthlyAmount = (monthly: string): number | null => {
    const num = Number(monthly.replace(/[^\d]/g, ''));
    return Number.isFinite(num) && num > 0 ? num : null;
  };

  // 从API获取price_id和套餐数字，通过卡片金额与 unit_amount 一致来匹配
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const res = await getAvailablePrices();
        const pricesData = res.data?.prices || [];

        if (pricesData.length === 0) return;

        // 按 unit_amount 建立映射：金额 → priceInfo
        const priceByAmount = new Map<number, PriceInfo>();
        for (const priceInfo of pricesData) {
          priceByAmount.set(priceInfo.unit_amount, priceInfo);
        }

        // 按卡片上的金额匹配 API 数据
        const priceMap = new Map<Tier['id'], PriceInfo>();
        for (const tier of tiers) {
          const cardAmount = parseMonthlyAmount(tier.price.monthly);
          if (cardAmount === null) continue;
          const priceInfo = priceByAmount.get(cardAmount);
          if (priceInfo) priceMap.set(tier.id, priceInfo);
        }

        // 更新 priceId、bot_quota、message_quota（金额保持卡片原值）
        setTiers((prevTiers) =>
          prevTiers.map((tier) => {
            const priceInfo = priceMap.get(tier.id);
            if (!priceInfo) return tier;
            return {
              ...tier,
              priceId: priceInfo.price_id,
              features: buildFeatureList(tier, priceInfo.plan),
            };
          })
        );

        // 同步更新对比表
        updateFeatureTable(priceMap);
      } catch (error) {
        showNotification({
          color: 'red',
          message: '価格情報の取得に失敗しました',
        });
      }
    };

    fetchPrices();
  }, []);

  /** 根据 API 数据更新卡片 features 列表中的 bot_quota 和 message_quota */
  const buildFeatureList = (tier: Tier, plan: PriceInfo['plan']): string[] => {
    return tier.features
      .map((feature) => {
        // ボット数（排除「チャットボット」等复合词），只替换数字保留原文案格式
        if (feature.includes('ボット') && !feature.includes('チャットボット')) {
          if (plan.bot_quota === null) return feature.replace(/\d[\d,]*/g, '').replace('ボット', '無制限ボット');
          return feature.replace(/\d[\d,]*/, formatQuota(plan.bot_quota));
        }
        // メッセージ数，只替换数字保留原文案格式
        if (feature.includes('メッセージ')) {
          if (plan.message_quota === null) return '無制限メッセージ';
          return feature.replace(/\d[\d,]*/, formatQuota(plan.message_quota));
        }
        // マルチモーダル分析，只替换括号内数字保留原文案格式，0表示不可用则移除
        if (feature.includes('マルチモーダル分析')) {
          if (plan.multi_modal_parsing_quota === 0) return null;
          if (plan.multi_modal_parsing_quota === null) return '無制限マルチモーダル分析';
          return feature.replace(/\d[\d,]*/, formatQuota(plan.multi_modal_parsing_quota));
        }
        return feature;
      })
      .filter(Boolean) as string[];
  };

  /** 更新对比表中 bot / message 行 */
  const updateFeatureTable = (priceMap: Map<Tier['id'], PriceInfo>) => {
    setFeatures((prev) =>
      prev.map((feature) => {
        if (feature.name === 'ボット数') {
          const updated = { ...feature.tiers };
          priceMap.forEach((priceInfo, tierId) => {
            updated[tierId] = formatQuota(priceInfo.plan.bot_quota);
          });
          return { ...feature, tiers: updated };
        }
        if (feature.name === '月間メッセージ数') {
          const updated = { ...feature.tiers };
          priceMap.forEach((priceInfo, tierId) => {
            const q = priceInfo.plan.message_quota;
            updated[tierId] = q === null ? '無制限' : `${formatQuota(q)}件/月`;
          });
          return { ...feature, tiers: updated };
        }
        if (feature.name === 'マルチモーダル分析') {
          const updated = { ...feature.tiers };
          priceMap.forEach((priceInfo, tierId) => {
            const q = priceInfo.plan.multi_modal_parsing_quota;
            updated[tierId] = q === null ? '無制限' : q === 0 ? false : `${formatQuota(q)}アイテム`;
          });
          return { ...feature, tiers: updated };
        }
        return feature;
      })
    );
  };

  // 判断购买按钮是否应被禁用（enterprise 为联系按钮不禁用，其余在有活跃订阅时禁用）
  const isPurchaseDisabled = (tier: Tier): boolean => {
    if (tier.id === 'enterprise') return false;
    return hasActiveSubscription;
  };

  // 处理购买/联系按钮点击
  const handlePurchase = async (tier: Tier) => {
    // Enterprise套餐跳转到联系页面
    if (tier.id === 'enterprise') {
      navigate('/contact');
      return;
    }

    // 有活跃订阅时阻止购买
    if (hasActiveSubscription) return;

    if (!tier.priceId) {
      showNotification({
        color: 'orange',
        message: 'この料金プランは現在ご利用いただけません。お問い合わせください。',
      });
      return;
    }

    // 检查登录状态（Auth0 模式下通过 Cookie 判断）
    const nowTime = new Date().getTime();
    const expiresTime = Number(Cookies.get(KEY_MYGPT_TOKEN_EXPIRESIN()));

    if (!expiresTime || expiresTime <= nowTime) {
      // 未登录：弹出登录/注册选择 Modal，两者跳回当前完整 URL（保留 ?promo=）
      setAuthChoiceOpen(true);
      return;
    }

    // v0.5: 不再直接调 Stripe，先打开订单确认 Modal
    setCheckoutLoading(tier.id);
    setConfirmPriceId(tier.priceId);
  };

  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  return (
    <div className="">
      <AuthChoiceModal opened={authChoiceOpen} onClose={() => setAuthChoiceOpen(false)} />

      {/* v0.5: 订单确认 Modal，所有用户点订阅都先弹此 Modal */}
      <OrderConfirmModal
        opened={!!confirmPriceId}
        priceId={confirmPriceId || ''}
        initialPromo={urlPromo}
        successUrl={`${origin}/price?payment=success`}
        cancelUrl={`${origin}/price?payment=cancel`}
        onClose={() => {
          setConfirmPriceId(null);
          setCheckoutLoading(null);
        }}
      />

      {/* Hero Section */}
      <section className="relative isolate pt-24">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
          <motion.div
            className="mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="mx-auto max-w-4xl text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
                シンプルな料金体系
                <br />
                <span className={classes.gradientText}>必要な機能を、必要なだけ</span>
              </h1>
              <p className="mt-6 text-lg leading-8 text-gray-600">ビジネスの規模に合わせて選べる柔軟なプラン。</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-24 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          {/* 已有订阅提示 */}
          {hasActiveSubscription && (
            <Alert icon={<IconInfoCircle size={18} />} color="violet" variant="light" radius="md" mb={50}>
              現在ご利用中のプランがございます。別のプランをご希望の場合は、マイページから現在のサブスクリプションを解約いただくか、
              <span
                className="underline cursor-pointer font-medium hover:opacity-80"
                onClick={() => navigate('/contact')}
              >
                お問い合わせ
              </span>
              いただけますようお願いいたします。
            </Alert>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {tiers.map((tier) => (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className={`relative flex flex-col justify-between rounded-2xl bg-white p-6 ring-1 ring-gray-200 ${
                  tier.mostPopular ? 'ring-2 ring-violet-500' : ''
                }`}
              >
                {tier.mostPopular && (
                  <p className="absolute left-1/2 -translate-x-1/2 -top-8 z-10 inline-flex rounded-full bg-violet-500/10 px-2 py-1 text-xs font-semibold leading-4 text-violet-500 ring-1 ring-inset ring-violet-500/10 shadow">
                    人気プラン
                  </p>
                )}
                <div>
                  <h3 className="text-xl font-bold tracking-tight text-gray-900">{tier.name}</h3>
                  <p className="mt-4 flex items-baseline gap-x-1">
                    <span className="text-3xl font-bold tracking-tight text-gray-900">
                      {tier.id === 'enterprise' ? (
                        'お問い合わせ'
                      ) : (
                        <>
                          ¥{tier.price.monthly}
                          <span className="text-sm font-semibold leading-6 text-gray-600">/月</span>
                        </>
                      )}
                    </span>
                  </p>
                  <p className="mt-4 text-sm leading-6 text-gray-600">{tier.description}</p>
                  <ul role="list" className="mt-6 space-y-2 text-sm leading-6 text-gray-600">
                    {tier.features.map((feature, idx) => (
                      <li key={`${tier.id}-feature-${idx}`} className="flex gap-x-2">
                        <CheckIcon className="h-5 w-4 flex-none text-violet-500 mt-0.5" />
                        <span className="text-xs">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-6">
                  <button
                    onClick={() => handlePurchase(tier)}
                    disabled={checkoutLoading === tier.id || isPurchaseDisabled(tier)}
                    className="w-full rounded-lg bg-violet-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-violet-500/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {checkoutLoading === tier.id ? '処理中...' : tier.cta}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="py-24 sm:py-20 bg-gray-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <h2 className="text-base font-semibold leading-7 text-violet-500">詳細な機能比較</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">すべての機能を比較</p>
          </div>
          <div className="mt-16 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">
                    機能
                  </th>
                  {tiers.map((tier) => (
                    <th
                      key={tier.id}
                      scope="col"
                      className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
                    >
                      {tier.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {features.map((feature, idx) => (
                  <tr key={`feature-${idx}`}>
                    <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900">{feature.name}</td>
                    {tiers.map((tier) => (
                      <td key={`${tier.id}-${idx}`} className="px-3 py-4 text-center text-sm text-gray-500">
                        {typeof feature.tiers[tier.id] === 'boolean' ? (
                          feature.tiers[tier.id] ? (
                            <CheckIcon className="mx-auto h-5 w-5 text-violet-500" />
                          ) : (
                            <XMarkIcon className="mx-auto h-5 w-5 text-gray-400" />
                          )
                        ) : (
                          feature.tiers[tier.id]
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Payment Success Modal */}
      <Modal
        opened={paymentSuccessModalOpened}
        onClose={() => setPaymentSuccessModalOpened(false)}
        title="支払い成功"
        centered
        size="md"
      >
        <div className="text-center py-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <CheckIcon className="h-6 w-6 text-green-600" />
          </div>
          <Text size="lg" fw={600} mb="md">
            ご購入ありがとうございます！
          </Text>
          <Text size="sm" c="dimmed" mb="xl">
            お支払いが正常に完了しました。
            <br />
            サブスクリプションが有効になりました。
          </Text>
          <Group justify="center" gap="md">
            <Button variant="outline" onClick={() => setPaymentSuccessModalOpened(false)}>
              閉じる
            </Button>
            <Button
              onClick={() => {
                window.location.href = `${import.meta.env.VITE_ADMIN_URL}/account`;
              }}
            >
              管理画面へ
            </Button>
          </Group>
        </div>
      </Modal>
    </div>
  );
};

export default Price;
